import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUser } from '@stackframe/react';
import { Loader } from '../components/common';

/**
 * OAuth Callback Handler
 * Handles the OAuth redirect and errors gracefully
 */
const OAuthCallback = () => {
  const navigate = useNavigate();
  const { user: authUser, isAdmin, setUser } = useAuth();
  const stackUser = useUser();
  const [error, setError] = useState(null);
  const [waitingForSync, setWaitingForSync] = useState(true);
  const timeoutRef = useRef(null);
  const hasSynced = useRef(false);

  // Log component mount and user state changes
  useEffect(() => {
    console.log('🔵 OAuthCallback mounted');
    console.log('📊 Initial state:', {
      stackUser: stackUser ? 'exists' : 'null',
      stackUserEmail: stackUser?.primaryEmail,
      authUser: authUser ? 'exists' : 'null',
      authUserEmail: authUser?.email,
      localStorage: localStorage.getItem('auth_user') ? 'has data' : 'empty',
      cookies: document.cookie ? 'has cookies' : 'no cookies'
    });
  }, []);

  useEffect(() => {
    console.log('🔄 User state changed:', {
      stackUser: stackUser?.primaryEmail || 'null',
      authUser: authUser?.email || 'null',
      hasSynced: hasSynced.current
    });
  }, [stackUser, authUser]);

  useEffect(() => {
    // Check for OAuth errors in URL
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (errorParam) {
      console.error('OAuth error:', errorParam, errorDescription);
      setError(errorDescription || 'OAuth authentication failed');
      setWaitingForSync(false);
      return;
    }

    console.log('🔍 OAuth callback state:', {
      stackUser: stackUser?.primaryEmail || 'null',
      authUser: authUser?.email || 'null',
      hasSynced: hasSynced.current,
      pathname: window.location.pathname,
      search: window.location.search,
      hasStackCookies: document.cookie.includes('stack-access')
    });

    // PRIORITY CHECK: If we already have authUser from localStorage, redirect immediately
    if (authUser && !hasSynced.current) {
      console.log('✅ AuthUser already exists from localStorage, redirecting immediately');
      hasSynced.current = true;
      setTimeout(() => {
        navigate(isAdmin ? '/admin' : '/user-dashboard', { replace: true });
      }, 100);
      return;
    }

    // IMMEDIATE SYNC: If we have Stack user but no auth user, sync immediately
    if (stackUser && !authUser && !hasSynced.current) {
      console.log('🔄 Immediate sync: Creating auth user from Stack user');
      hasSynced.current = true;
      
      const email = stackUser.primaryEmail;
      const displayName = stackUser.displayName || email?.split('@')[0];
      const isAdminUser = email === 'rao.vinu@gmail.com';
      
      const user = {
        id: stackUser.id,
        email: email,
        displayName: displayName,
        provider: 'google',
        role: isAdminUser ? 'admin' : 'user',
        createdAt: stackUser.createdAt || new Date().toISOString()
      };
      
      // Store in AuthContext and localStorage
      setUser(user);
      localStorage.setItem('auth_user', JSON.stringify(user));
      
      // Sync to database (non-blocking)
      fetch('/api', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'user.create',
          data: {
            email: user.email,
            display_name: user.displayName,
            role: user.role,
            provider: user.provider,
            stack_user_id: stackUser.id
          }
        })
      }).catch(error => {
        console.error('Error syncing user to database:', error);
      });
      
      // Redirect immediately after sync
      console.log('✅ User synced! Redirecting to', isAdminUser ? '/admin' : '/user-dashboard');
      setTimeout(() => {
        navigate(isAdminUser ? '/admin' : '/user-dashboard', { replace: true });
      }, 100);
      return;
    }

    // If we have both Stack user and AuthContext user, redirect
    if (stackUser && authUser) {
      console.log('✅ Both users ready! Redirecting to', isAdmin ? '/admin' : '/user-dashboard');
      navigate(isAdmin ? '/admin' : '/user-dashboard', { replace: true });
      return;
    }

    // If we have Stack cookies but no Stack user yet, wait for it to load
    const hasStackCookies = document.cookie.includes('stack-access');
    if (hasStackCookies && !stackUser && !authUser && !timeoutRef.current) {
      console.log('⏳ Stack cookies found, waiting for Stack user to initialize...');
      timeoutRef.current = setTimeout(() => {
        if (!stackUser && !authUser) {
          console.error('❌ Timeout: Stack user not initialized after 5 seconds despite cookies');
          console.log('Checking if we can extract user from cookies...');
          // Try to redirect anyway - Stack might initialize on next page
          navigate('/user-dashboard', { replace: true });
        }
      }, 5000);
    }
    
    // If no Stack cookies and no users after 3 seconds, redirect home
    if (!hasStackCookies && !stackUser && !authUser && !timeoutRef.current) {
      timeoutRef.current = setTimeout(() => {
        console.error('❌ Timeout: No Stack cookies or users found');
        console.log('Redirecting to home page...');
        navigate('/', { replace: true });
      }, 3000);
    }

    // Set timeout to show error if sync takes too long (8 seconds)
    if (waitingForSync && stackUser && !timeoutRef.current) {
      timeoutRef.current = setTimeout(() => {
        if (!authUser) {
          console.error('❌ Timeout: AuthContext not synced after 8 seconds');
          setError('Authentication is taking longer than expected. Please refresh the page.');
          setWaitingForSync(false);
        }
      }, 8000);
    }

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [stackUser, authUser, isAdmin, navigate, waitingForSync]);

  const handleRetry = () => {
    navigate('/');
  };

  if (error) {
    // Clear timeout if showing error
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-800 rounded-xl p-8 shadow-2xl">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-white mb-2">Sign In Error</h2>
            <p className="text-slate-300 text-sm mb-4">{error}</p>
            <div className="bg-slate-700 rounded-lg p-4 mb-4 text-left">
              <p className="text-slate-300 text-xs mb-2">Common causes:</p>
              <ul className="text-slate-400 text-xs space-y-1 list-disc list-inside">
                <li>Page was refreshed during sign in</li>
                <li>Browser blocked cookies</li>
                <li>Network connection interrupted</li>
              </ul>
            </div>
          </div>
          <button
            onClick={handleRetry}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
      <div className="text-center">
        <Loader size="lg" />
        <p className="text-white mt-4 text-lg" suppressHydrationWarning>Completing sign in...</p>
        <p className="text-slate-400 mt-2 text-sm" suppressHydrationWarning>Please wait, do not refresh the page</p>
        {stackUser && !authUser && (
          <p className="text-slate-500 mt-4 text-xs" suppressHydrationWarning>Syncing user data...</p>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;
